/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';

import { IUploadedAsset, Upload } from '@components/upload';
import { UPLOAD_ALLOWED_AS_AVATAR } from '@constants';
import { IUploadImage } from '@interface/media';
import { uploadFile } from '@lib/ipfs/adapters/infura';
import { getCroppedImg } from '@utils/canvas';
import { Box, Button, Dialog, Field, ImageEdit, Slider, Step } from '@versumstudios/ui';

export interface ICoverEdit {
  path?: string;
  callback?: (next: IUploadImage) => void;
}

export const CoverEdit: FC<ICoverEdit> = ({ path, callback }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<IUploadedAsset | undefined>();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [rotation, setRotation] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | undefined>();
  const [loading, setLoading] = useState(false);

  const onCropComplete = useCallback((_nextCroppedArea: Area, nextCroppedAreaPixels: Area) => {
    setCroppedAreaPixels(nextCroppedAreaPixels);
  }, []);

  const reset = () => {
    setOpen(false);
    setFile(undefined);
    setLoading(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    if (file?.uri && croppedAreaPixels) {
      const nextCroppedImage = await getCroppedImg(file?.uri, croppedAreaPixels, rotation, 1500);
      if (nextCroppedImage) {
        const cid = await uploadFile(nextCroppedImage as File, true, false);
        if (callback) {
          const blob = URL.createObjectURL(nextCroppedImage);
          callback({ blob, ipfs: `ipfs://${cid}` });
          reset();
        }
      }
    }
  };

  const handleCancel = () => {
    reset();
  };

  const handleUploadChange = (nextFile: IUploadedAsset) => {
    setFile(nextFile);
  };

  return (
    <>
      <ImageEdit css={{ width: '100%', height: '100%' }} onClick={() => setOpen(true)}>
        {path && <img src={path} alt="cover" />}
      </ImageEdit>

      <Dialog
        title="Edit Cover"
        description="Recommended size 1000x300px"
        open={open}
        onOpenChange={(next: boolean) => setOpen(next)}
      >
        <div>
          {file ? (
            <>
              <Box
                css={{
                  position: 'relative',
                  width: '100%',
                  minHeight: 200,
                  marginBottom: '$space$3',
                }}
              >
                <Cropper
                  image={file.uri}
                  crop={crop}
                  rotation={rotation}
                  zoom={zoom}
                  aspect={1000 / 300}
                  objectFit="contain"
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  // cropShape="round"
                  showGrid={false}
                />
              </Box>
              <Field uid="zoom" label="Zoom">
                <Slider
                  label="zoom-slider"
                  min={1}
                  max={3}
                  value={[zoom]}
                  onChange={(nextZoom: number) => setZoom(nextZoom)}
                  scrubber
                />
              </Field>
              <Field uid="rotate" label="Rotation">
                <Slider
                  label="rotation-slider"
                  min={0}
                  max={360}
                  step={1}
                  value={[rotation]}
                  onChange={(nextRotation: number) => setRotation(nextRotation)}
                  scrubber
                />
              </Field>
              <Box
                css={{
                  flexDirection: 'column',
                  '& > *': {
                    marginTop: '$space$4',
                  },
                }}
              >
                <Button full onClick={handleConfirm} disabled={loading}>
                  {loading ? <Step progress /> : <>Confirm</>}
                </Button>
                <Button full variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Field uid="image">
                <Upload onChange={handleUploadChange} restrict={UPLOAD_ALLOWED_AS_AVATAR} />
              </Field>
              <Box
                css={{
                  flexDirection: 'column',
                  '& > *': {
                    marginTop: '$space$4',
                  },
                }}
              >
                <Button full variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </div>
      </Dialog>
    </>
  );
};
